import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { BASE, getAuthHeaders } from '@/api/client'

// Types
export interface TelegramBot {
  id: string
  bot_username: string
  bot_user_id: number
  is_active: boolean
  last_verified_at: string | null
  groups_count: number
  contacts_count: number
  created_at: string
}

export interface TelegramGroup {
  id: string
  chat_id: number
  title: string
  type: string
  member_count: number
  joined_at: string
}

export interface TelegramContact {
  id: number
  telegram_user_id: number
  first_name: string
  last_name: string | null
  username: string | null
  last_seen_at: string
}

export interface TelegramKeywordRule {
  id: string
  keyword: string
  match_type: 'contains' | 'exact'
  action: 'dm' | 'reply'
  response_text: string
  response_image: string | null
  button_text: string | null
  button_url: string | null
  group_id: string | null
  group?: { id: string; title: string }
  priority: number
  cooldown_minutes: number
  is_active: boolean
}

export interface TelegramBroadcastHistory {
  id: string
  message: string
  target: 'contacts' | 'group'
  status: string
  total_contacts: number
  sent_count: number
  failed_count: number
  scheduled_at: string | null
  created_at: string
}

const headers = () => ({ ...getAuthHeaders(), 'Content-Type': 'application/json' })

async function api(path: string, options?: RequestInit) {
  const res = await fetch(`${BASE}/telegram${path}`, { headers: headers(), ...options })
  if (!res.ok) {
    const data = await res.json().catch(() => ({}))
    throw new Error(data.error || data.message || `HTTP ${res.status}`)
  }
  return res.json()
}

// Bots
export function useTelegramBots() {
  return useQuery<TelegramBot[]>({ queryKey: ['telegram-bots'], queryFn: () => api('/bots') })
}

export function useAddBot() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (token: string) => api('/bots', { method: 'POST', body: JSON.stringify({ token }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['telegram-bots'] }),
  })
}

export function useDeleteBot() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api(`/bots/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['telegram-bots'] }),
  })
}

// Groups
export function useTelegramGroups(botId: string | null) {
  return useQuery<TelegramGroup[]>({
    queryKey: ['telegram-groups', botId],
    queryFn: () => api(`/bots/${botId}/groups`),
    enabled: !!botId,
  })
}

export function useDeleteGroup() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api(`/groups/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['telegram-groups'] }),
  })
}

// Contacts
export function useTelegramContacts(botId: string | null, search?: string) {
  return useQuery({
    queryKey: ['telegram-contacts', botId, search],
    queryFn: () => api(`/bots/${botId}/contacts${search ? `?search=${search}` : ''}`),
    enabled: !!botId,
  })
}

export function useTelegramContactsCount(botId: string | null) {
  return useQuery<{ count: number }>({
    queryKey: ['telegram-contacts-count', botId],
    queryFn: () => api(`/bots/${botId}/contacts/count`),
    enabled: !!botId,
  })
}

// Keywords
export function useTelegramKeywords(botId: string | null) {
  return useQuery<TelegramKeywordRule[]>({
    queryKey: ['telegram-keywords', botId],
    queryFn: () => api(`/bots/${botId}/keywords`),
    enabled: !!botId,
  })
}

export function useAddKeyword(botId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Partial<TelegramKeywordRule>) =>
      api(`/bots/${botId}/keywords`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['telegram-keywords'] }),
  })
}

export function useUpdateKeyword() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & Partial<TelegramKeywordRule>) =>
      api(`/keywords/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['telegram-keywords'] }),
  })
}

export function useDeleteKeyword() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api(`/keywords/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['telegram-keywords'] }),
  })
}

// Broadcast
export function useSendTelegramBroadcast(botId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { message: string; image_url?: string; button_text?: string; button_url?: string; target: string; target_group_id?: string; scheduled_at?: string }) =>
      api(`/bots/${botId}/broadcast`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['telegram-broadcast-history'] }),
  })
}

export function useTelegramBroadcastHistory(botId: string | null) {
  return useQuery<TelegramBroadcastHistory[]>({
    queryKey: ['telegram-broadcast-history', botId],
    queryFn: () => api(`/bots/${botId}/broadcast/history`),
    enabled: !!botId,
  })
}
